// main.jsx / index.jsx
import React from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import router from "./routes";

import AppErrorBoundary from "./components/errors/AppErrorBoundary";
import { DexieProvider } from "./providers/DexieProvider";
import { ThemeProvider } from "./providers/ThemeProvider";
import { AuthProvider } from "./providers/AuthProvider";
import AppBootstrap from "./init/AppBootstrap";
import ThemeSync from "./utils/ThemeSync"; //bridge

import "./assets/styles/index.css";

const appEl = document.getElementById("body");
if (!appEl) throw new Error("Element body not found");

/**
 * Application Root Render
 *
 * Initializes and renders the React application with a nested provider hierarchy.
 * The order of providers is critical as inner components depend on outer context.
 *
 * EXECUTION ORDER (outside-in):
 * =============================
 * 1. React.StrictMode
 *    - Wrapper for development checks (double-invokes effects, highlights issues)
 *    - Removed in production builds automatically
 *
 * 2. AppErrorBoundary
 *    - Catches JavaScript errors anywhere in the child component tree
 *    - Prevents entire app crashes by displaying fallback UI
 *
 * 3. DexieProvider
 *    - Provides IndexedDB database context via Dexie.js
 *    - Makes database instance available to all child components
 *
 * 4. ThemeProvider
 *    - Provides theme state (mode, accent, etc.) to the app
 *    - uses local storage to mirror theme settings
 *    - Must wrap ThemeSync since ThemeSync consumes theme context
 *
 * 5. ThemeSync
 *    - Bridge component that syncs theme state with database
 *    - Positioned here because it needs BOTH ThemeProvider AND DexieProvider
 *    - Not a provider itself, just a synchronization component
 *
 * 6. AuthProvider
 *    - Provides authentication state and methods
 *    - Note: If this used router hooks (useLocation, useNavigate), it would
 *      need to be moved BELOW RouterProvider
 *
 * 7. AppBootstrap
 *    - Handles app initialization logic (loading states, setup tasks)
 *    - Runs after all core providers are available
 *
 * 8. RouterProvider (innermost)
 *    - Provides routing context and renders matched routes
 *    - All other contexts are available to routed components
 *
 * DEPENDENCY CHAIN:
 * ================
 * RouterProvider depends on → AppBootstrap depends on → AuthProvider depends on →
 * ThemeProvider & DexieProvider ← both needed by ThemeSync
 *
 * @important Provider order matters! Inner components can only access contexts
 *            from providers that wrap them. Reversing the order will break
 *            dependencies.
 */
ReactDOM.createRoot(appEl).render(
  <React.StrictMode>
    <AppErrorBoundary>
      <DexieProvider>
        <ThemeProvider>
          {/* Bridge lives under BOTH Theme & Dexie */}
          <ThemeSync />
          {/* If AuthProvider uses router hooks like useLocation, put AuthProvider BELOW RouterProvider instead */}
          <AuthProvider>
            <AppBootstrap>
              <RouterProvider router={router} />
            </AppBootstrap>
          </AuthProvider>
        </ThemeProvider>
      </DexieProvider>
    </AppErrorBoundary>
  </React.StrictMode>
);

//previous
// // import React from "react";
// import ReactDOM from "react-dom/client";
// import { RouterProvider } from "react-router-dom";
// import router from "./routes";
// import { AuthProvider } from "./providers/AuthProvider";
// import { ThemeProvider } from "./providers/ThemeProvider";
// import AppErrorBoundary from "./components/Errors/AppErrorBoundary";
// import { DexieProvider } from "./providers/DexieProvider";
// import AppBootstrap from "./init/AppBootstrap";
// import ThemeSync from "./utils/ThemeSync";

// // import "bootstrap/dist/css/bootstrap.min.css";
// import "./assets/styles/index.css";

// const appEl = document.getElementById("root");
// if (!appEl) throw new Error('Root element "#root" not found');

// ReactDOM.createRoot(appEl).render(
//   <React.StrictMode>
//     {/* Put the global boundary outermost so it can catch provider/render errors */}
//     <AppErrorBoundary>
//       <ThemeProvider>
//         <DexieProvider>
//           <ThemeSync>
//             {/* Router outside providers so providers can use router hooks if needed */}
//             <AuthProvider>
//               <AppBootstrap>
//                 <RouterProvider router={router}></RouterProvider>
//               </AppBootstrap>
//             </AuthProvider>
//           </ThemeSync>
//         </DexieProvider>
//       </ThemeProvider>
//     </AppErrorBoundary>
//   </React.StrictMode>
// );

// // If you want your app to work offline and load faster, you can change
// // unregister() to register() below. Note this comes with some pitfalls.
// // Learn more about service workers: https://cra.link/PWA
// // serviceWorkerRegistration.register();

// // If you want to start measuring performance in your app, pass a function
// // to log results (for example: reportWebVitals(console.log))
// // or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// // reportWebVitals(console.log);
